// D132 (§3.2) — "my button did nothing" becomes "come around the side".
//
// The D80 back-cone shield refuses a tackle or a trap poke from directly behind the carrier, and it does
// so SILENTLY: the verb fires, the animation plays, nothing happens, and there is no way to learn the
// rule from playing. This is the render-side cue, and it is render-side ONLY — a pure predicate over the
// ViewState the client already has, producing no event and no state (invariant 8). It reads the same
// geometry `possession.ts::shieldedFromBehind` does, so it can never light up for a tackle the sim would
// have allowed, nor stay dark on one it refused.

import type { ViewState } from './viewState';

export interface ConeFlick {
  /** The protected carrier — the arc is drawn on HIS back, which is the thing you must get around. */
  readonly x: number;
  readonly y: number;
  readonly facing: number;
}

/** The verbs the back cone refuses. TACKLE_SLIDE is deliberately absent: the committal one beats it. */
const CONE_GATED = new Set(['TACKLE_STAND', 'TRAP']);

/**
 * The arc to flick this frame, or null. Fires when the CONTROLLED player is mid-tackle/trap against an
 * opponent carrier whose back cone he is standing in.
 */
export function coneRejection(view: ViewState, shieldConeDot: number): ConeFlick | null {
  const me = view.entities.find((e) => e.isControlled);
  if (!me || !CONE_GATED.has(me.action)) return null;
  const poss = view.ball.possessor;
  if (poss === null || poss === me.id) return null;
  const carrier = view.entities.find((e) => e.id === poss);
  if (!carrier || carrier.team === me.team) return null;
  const dx = me.pos.x - carrier.pos.x;
  const dy = me.pos.y - carrier.pos.y;
  const dd = Math.hypot(dx, dy);
  if (dd < 1e-6) return null;
  const behind = (Math.cos(carrier.facing) * dx + Math.sin(carrier.facing) * dy) / dd < -shieldConeDot;
  return behind ? { x: carrier.pos.x, y: carrier.pos.y, facing: carrier.facing } : null;
}
